import React, { useState, useEffect, useRef } from 'react'
import {
	Card,
	CardHeader,
	Text,
	Button,
	Textarea,
	Spinner,
	Badge,
	MessageBar,
	MessageBarBody,
	MessageBarTitle,
	Avatar,
	tokens,
	makeStyles
} from '@fluentui/react-components'
import { BotAvatar } from '../common/BotAvatar'
import {
	SendRegular,
	ClockRegular,
	TabsRegular,
	BotRegular,
	CallRegular,
	ChatRegular,
} from '@fluentui/react-icons'
import { streamChat, RateLimitError, type ChatMessage } from '../../services/chatService'
import { VoiceCallInline } from '../voice/VoiceCall'

const useStyles = makeStyles({
	container: {
		padding: '1rem',
		maxWidth: '800px',
		margin: '0 auto',
		gap: '1rem',
		display: 'flex',
		flexDirection: 'column'
	},
	header: {
		textAlign: 'center',
		marginBottom: '1rem'
	},
	chatMessages: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
	},
	message: {
		display: 'flex',
		gap: '0.75rem',
		padding: '0.75rem',
		borderRadius: tokens.borderRadiusMedium,
		backgroundColor: tokens.colorNeutralBackground2
	},
	messageContent: {
		flex: 1
	},
	messageHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.5rem',
		marginBottom: '0.25rem'
	},
	inputContainer: {
		display: 'flex',
		gap: '0.5rem',
		alignItems: 'flex-end',
		paddingTop: '0.75rem',
		borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
		flexShrink: 0,
	},
	messageInput: {
		flex: 1
	},
	featuresGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
		gap: '1rem',
		marginTop: '1rem'
	},
	featureCard: {
		padding: '1.5rem',
		textAlign: 'center'
	},
	modeToggle: {
		display: 'flex',
		gap: '0.5rem',
		alignItems: 'center',
	},
})

interface UiMessage {
	id: string
	role: 'user' | 'assistant'
	content: string
	streaming?: boolean
}

type TabMode = 'chat' | 'voice'


export const TeamsTab: React.FC = () => {
	const styles = useStyles()
	const [messages, setMessages] = useState<UiMessage[]>([])
	const [input, setInput] = useState('')
	const [isSending, setIsSending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [mode, setMode] = useState<TabMode>('voice')
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const abortRef = useRef<AbortController | null>(null)

	useEffect(() => {
		if (mode === 'chat' && messages.length > 0) {
			messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
		}
	}, [messages, mode])

	useEffect(() => {
		const handler = (e: Event) => {
			const { mode: requested } = (e as CustomEvent<{ mode: TabMode }>).detail
			setMode(requested)
		}
		document.addEventListener('cv:open-tab', handler)
		return () => document.removeEventListener('cv:open-tab', handler)
	}, [])

	const sendMessage = async () => {
		const text = input.trim()
		if (!text || isSending) return

		setInput('')
		setError(null)
		setIsSending(true)

		const userMsg: UiMessage = { id: crypto.randomUUID(), role: 'user', content: text }
		const botId = crypto.randomUUID()
		const botMsg: UiMessage = { id: botId, role: 'assistant', content: '', streaming: true }

		setMessages(prev => [...prev, userMsg, botMsg])

		const ASSISTANT_SUMMARY_LEN = 300
		const HISTORY_PAIRS = 3
		const settled = [...messages, userMsg].filter(m => m.content !== '')
		const history: ChatMessage[] = settled
			.slice(-HISTORY_PAIRS * 2)
			.map(m => ({
				role: m.role,
				content: m.role === 'assistant' && m.content.length > ASSISTANT_SUMMARY_LEN
					? m.content.slice(0, ASSISTANT_SUMMARY_LEN) + '…'
					: m.content,
			}))

		abortRef.current = new AbortController()

		try {
			for await (const token of streamChat(history, abortRef.current.signal)) {
				setMessages(prev =>
					prev.map(m => (m.id === botId ? { ...m, content: m.content + token } : m)),
				)
			}
		} catch (err) {
			if (err instanceof RateLimitError) {
				setError('I\'m popular today - try again in a minute.')
			} else if (err instanceof Error && err.name !== 'AbortError') {
				setError(err.message)
			}
			setMessages(prev => prev.filter(m => !(m.id === botId && m.content === '')))
		} finally {
			setMessages(prev => prev.map(m => (m.id === botId ? { ...m, streaming: false } : m)))
			setIsSending(false)
			abortRef.current = null
		}
	}


	const features = [
		{
			title: 'Interactive Tabs',
			description: 'Custom Teams tabs showcasing portfolio and technical demos',
			icon: <TabsRegular style={{ fontSize: '2rem', color: tokens.colorPaletteBlueBorderActive }} />
		},
		{
			title: 'CV Bot',
			description: 'Ask anything about Martin\'s skills, experience, and availability',
			icon: <BotRegular style={{ fontSize: '2rem', color: tokens.colorPaletteGreenBorderActive }} />
		},
		{
			title: 'Real-time Streaming',
			description: 'Live token-by-token responses via Cloudflare Workers AI',
			icon: <ClockRegular style={{ fontSize: '2rem', color: tokens.colorPalettePurpleBorderActive }} />
		}
	]

	return (
		<div className={styles.container}>
			<Card className={styles.header}>
				<CardHeader
					header={
						<div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
							<Text size={600} weight="bold">
								Talk or chat with Martin&apos;s CV
							</Text>
							<div className={styles.modeToggle}>
								<Button
									appearance={mode === 'voice' ? 'primary' : 'subtle'}
									icon={<CallRegular />}
									size="large"
									onClick={() => setMode('voice')}
									aria-label="Switch to voice mode"
									aria-pressed={mode === 'voice'}
								>
									Talk
								</Button>
								<Button
									appearance={mode === 'chat' ? 'primary' : 'subtle'}
									icon={<ChatRegular />}
									size="large"
									onClick={() => setMode('chat')}
									aria-label="Switch to chat mode"
									aria-pressed={mode === 'chat'}
								>
									Chat
								</Button>
							</div>
						</div>
					}
					description="Ask about skills, experience, availability, or anything else in the CV"
				/>
			</Card>

			{error && (
				<MessageBar intent="error">
					<MessageBarBody>
						<MessageBarTitle>Error</MessageBarTitle>
						{error}
					</MessageBarBody>
				</MessageBar>
			)}

			<Card>
				{mode === 'voice' ? (
					<VoiceCallInline onClose={() => setMode('chat')} />
				) : (
					<div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '440px' }}>
						<Text size={400} weight="semibold" block>CV Assistant</Text>

						{/* Messages — grows to fill, scrolls when full; min-height:0 lets flex shrink past content size */}
						<div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
							<div className={styles.chatMessages}>
								{messages.length === 0 ? (
									<Text style={{ textAlign: 'center', color: tokens.colorNeutralForeground3, padding: '1rem 0' }}>
										Ask anything about Martin&apos;s background.
										Try: &ldquo;What is your SPFx experience?&rdquo; or &ldquo;Are you available for remote work?&rdquo;
									</Text>
								) : (
									messages.map((message) => (
										<div key={message.id} className={styles.message}>
											{message.role === 'assistant'
												? <BotAvatar size={32} badge={{ status: 'available' }} />
												: <Avatar name="You" badge={{ status: 'busy' }} />
											}
											<div className={styles.messageContent}>
												<div className={styles.messageHeader}>
													<Text weight="semibold">
														{message.role === 'user' ? 'You' : 'Martin\'s CV'}
													</Text>
													{message.role === 'assistant' && (
														<Badge appearance="filled" color="success">AI</Badge>
													)}
													{message.streaming && <Spinner size="tiny" />}
												</div>
												<Text>
													{message.content || (message.streaming ? '...' : '')}
												</Text>
											</div>
										</div>
									))
								)}
								<div ref={messagesEndRef} />
							</div>
						</div>

						<div className={styles.inputContainer}>
							<Textarea
								className={styles.messageInput}
								placeholder="Ask about Martin's skills, experience, or availability..."
								value={input}
								onChange={(_, data) => setInput(data.value)}
								rows={2}
								resize="vertical"
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault()
										sendMessage()
									}
								}}
							/>
							<Button
								appearance="primary"
								icon={isSending ? <Spinner size="tiny" /> : <SendRegular />}
								disabled={!input.trim() || isSending}
								onClick={sendMessage}
							>
								{isSending ? 'Thinking...' : 'Send'}
							</Button>
						</div>
					</div>
				)}
			</Card>

			<div className={styles.featuresGrid}>
				{features.map((feature, index) => (
					<Card key={index} className={styles.featureCard}>
						{feature.icon}
						<Text size={500} weight="semibold" style={{ margin: '1rem 0 0.5rem 0' }}>
							{feature.title}
						</Text>
						<Text size={300}>{feature.description}</Text>
					</Card>
				))}
			</div>

			<Text size={200} style={{ color: tokens.colorNeutralForeground3, textAlign: 'center' }}>
				Responses are generated by Cloudflare Workers AI (Llama 3.3 70B).
				The bot answers questions about Martin&apos;s CV only.
			</Text>
		</div>
	)
}

export default TeamsTab
